import {
  AfterViewInit,
  ChangeDetectorRef,
  Component,
  OnDestroy,
  OnInit,
  ChangeDetectionStrategy
} from '@angular/core';

import { FormControl, FormGroup, ReactiveFormsModule } from '@angular/forms';
import { Artikel } from '@softline/application';
import { DynamicModule, FieldOkResultConverter } from '@softline/dynamic';
import { Modal, Validators,
  ValueConverterDirective} from '@softline/ui-core';
import {
  catchError,
  combineLatestWith,
  EMPTY,
  filter,
  of,
  startWith,
  Subscription,
  switchMap } from 'rxjs';
import { ProduktkalkulationService } from '../../services/produktkalkulation.service';
import { Arteh, Legierung, Material } from '../../types/produktkalkulation';

import { NumberInputComponent } from '@softline/ui-core';
@Component({
  selector: 'soft-material-dialog',
  imports: [DynamicModule, ReactiveFormsModule,
    NumberInputComponent,
    ValueConverterDirective
  ],
  templateUrl: './material.dialog.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrls: ['./material.dialog.css'] })
export class MaterialDialog
  implements OnInit, AfterViewInit, OnDestroy, Modal<Partial<Material>>
{
  artstammConverter = new FieldOkResultConverter(
    { id: 'id', cartikelnummer: 'nummer', artbez1: 'artbez1' },
    { mapKeysOnly: true }
  );
  chargeConverter = new FieldOkResultConverter(
    { id: 'id', chargennummer: 'legierung' },
    { mapKeysOnly: true }
  );
  artehConverter = new FieldOkResultConverter(
    { id: 'id', arteh: 'arteh' },
    { mapKeysOnly: true }
  );

  private subscription?: Subscription;

  form = new FormGroup({
    artikel: new FormControl<Artikel | null>(null, [Validators.required()]),
    legierung: new FormControl<Legierung | null>(null, [Validators.required()]),
    mengeCt: new FormControl<number | null>(null),
    mengeGramm: new FormControl<number | null>(null),
    menge: new FormControl<number | null>(null, [Validators.required()]),
    arteh: new FormControl<Arteh | null>(null, [Validators.required()]),
    gewicht: new FormControl<number | null>(null, [Validators.required()]),
    aufschlagArtikelgruppe: new FormControl<number | null>(null) });

  mode: 'add' | 'edit' = 'add';

  set value(value: Material) {
    this.mode = 'edit';

    this.form.patchValue({
      artikel: value?.artikel,
      legierung:
        value?.legierung && value?.legierung?.legierung
          ? { ...value?.legierung, chargennummer: value?.legierung?.legierung }
          : (null as any),
      mengeCt: +value?.mengeCt || null,
      menge: +value?.menge || null,
      arteh: value?.arteh,
      gewicht: value?.gewicht,
      aufschlagArtikelgruppe:
        value?.aufschlagArtikelgruppeDetailverkauf?.aufschlag ?? null });

    if (
      value &&
      (value?.legierungspflichtig === false || value?.legierungspflicht === false)
    ) {
      this.form.removeValidators(Validators.required());
    }

    this.templateValues = value;
  }

  templateValues: Partial<Material> | null = null;

  close!: (result: Partial<Material>) => void;

  constructor(
    private readonly service: ProduktkalkulationService,
    private cdRef: ChangeDetectorRef
  ) {}

  ngOnInit(): void {
    this.subscription = this.form.controls.menge.valueChanges
      .pipe(
        startWith(this.form.value.menge ?? 0),
        combineLatestWith(
          this.form.controls.artikel.valueChanges.pipe(
            startWith(this.form.value.artikel ?? null)
          )
        ),
        filter(([, o]) => !!o && !!o?.id),
        switchMap(([menge, value]) =>
          value && value?.id
            ? this.service
                .getTemplateValues({
                  idartstamm: value.id,
                  menge: menge ?? 0,
                  gewicht: this.form.value?.gewicht ?? 0,
                  mengeCt: this.form.value?.mengeCt ?? null })
                .pipe(catchError(() => of(null)))
            : of(null)
        ),
        startWith(null),
        catchError(() => EMPTY)
      )
      .subscribe((template: Partial<Material> | null) => {
        if (!template) {
          return;
        }

        this.templateValues = template;

        const values = {
          arteh: template?.arteh ?? undefined,
          gewicht: template?.gewicht ?? undefined,
          mengeCt: +((template as any)?.carat || ''),
          menge: +(template?.menge || ''),
          aufschlagArtikelgruppe:
            template?.aufschlagArtikelgruppeDetailverkauf?.aufschlag };

        if (template?.legierung) {
          values['legierung'] = template?.legierung;
        }

        this.form.patchValue(values, { onlySelf: true, emitEvent: false });

        if (template && template?.legierungspflichtig === false) {
          this.form.controls.legierung.clearValidators();
          this.form.controls.legierung.setValidators([]);
          this.form.controls.legierung.updateValueAndValidity();
        }

        this.cdRef.detectChanges();
      });
  }

  async ngAfterViewInit(): Promise<void> {}

  ngOnDestroy(): void {
    if (this.subscription && !this.subscription.closed) this.subscription.unsubscribe();

    this.subscription = undefined;
  }

  registerCloseHandler(handler: (result: Partial<Material>) => void): void {
    this.close = handler;
  }

  onSubmit(): void {
    if (this.form.invalid) return;

    const formValue = this.form.getRawValue();

    const value: Partial<Material> = {
      artikel: formValue.artikel ?? undefined,
      legierung: formValue.legierung ?? undefined,
      mengeCt: formValue.mengeCt ?? undefined,
      menge: formValue.menge + '',
      arteh: formValue.arteh ?? undefined,
      gewicht: formValue.gewicht ?? undefined,
      aufschlagArtikelgruppeDetailverkauf: {
        aufschlag: formValue.aufschlagArtikelgruppe ?? undefined } };

    this.close(value);
  }
}
